/**
 * Naamopdruk cart link
 * --------------------
 * De naamopdruk-toeslag (bv. €5) wordt als een aparte cart-regel toegevoegd,
 * gekoppeld aan de handschoen via de verborgen property `_variant_id` (de
 * gekozen handschoen-variant), `Naam` (de ingevoerde naam) en `Product`
 * (de titel van de handschoen).
 *
 * In de cart/drawer is de verwijderknop + quantity van die fee-regel verborgen
 * (zie main-cart.liquid / cart-drawer.liquid), zodat een klant hem niet los kan
 * weggooien om de naamopdruk gratis te krijgen.
 *
 * Dit script houdt de koppeling verder in stand na elke cart-wijziging:
 *   - fee-quantity wordt gelijkgetrokken met de gekoppelde handschoen;
 *   - een fee-regel wordt ALLEEN verwijderd als de bijbehorende handschoen
 *     echt niet meer in de cart zit (veilige matching: variant OF producttitel).
 *
 * Let op: dit is een theme-oplossing. Ze voorkomt het "gratis" maken via de UI,
 * maar dwingt de betaling niet server-side af. Voor harde handhaving is een
 * Shopify (Cart/Checkout Validation) Function nodig.
 */
(function () {
  'use strict';

  var busy = false;
  var pending = false;

  function routes() {
    return (window.FoxTheme && window.FoxTheme.routes) || {};
  }

  function isFee(item) {
    return !!(item && item.properties && item.properties['_variant_id']);
  }

  /**
   * Zoek de handschoen-regel die bij deze fee-regel hoort.
   * Match op variant-id OF producttitel (ruim, zodat we een fee nooit per
   * ongeluk als "wees" bestempelen terwijl de handschoen er nog is).
   * Retourneert null alleen als er echt geen bijbehorende handschoen meer is.
   */
  function parentGloveOf(fee, items) {
    var variantId = String(fee.properties['_variant_id'] || '');
    var name = String(fee.properties['Naam'] || '').trim();
    var productTitle = fee.properties['Product'];

    var candidates = items.filter(function (item) {
      if (isFee(item)) return false;
      var variantMatch = variantId && String(item.variant_id) === variantId;
      var titleMatch = productTitle && item.product_title === productTitle;
      return variantMatch || titleMatch;
    });

    if (candidates.length === 0) return null;

    // Voorkeur voor een handschoen die ook exact dezelfde naam draagt.
    var byName = candidates.find(function (item) {
      return (
        item.properties &&
        Object.keys(item.properties).some(function (key) {
          return String(item.properties[key]).trim() === name;
        })
      );
    });

    return byName || candidates[0];
  }

  /**
   * Bepaal welke fee-regels aangepast moeten worden.
   * @returns {Object} updates keyed by line item key -> gewenste quantity
   */
  function computeUpdates(items) {
    var updates = {};

    items.filter(isFee).forEach(function (fee) {
      var parent = parentGloveOf(fee, items);

      if (!parent) {
        // Handschoen is echt weg -> fee ook weg.
        updates[fee.key] = 0;
      } else if (parent.quantity !== fee.quantity) {
        // Aantal handschoenen gewijzigd -> fee volgt.
        updates[fee.key] = parent.quantity;
      }
    });

    return updates;
  }

  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  async function getCart() {
    // Altijd vers ophalen (geen cache + unieke query), anders werken we soms op
    // een oude cart-snapshot en loopt de fee-quantity een stap achter.
    var url = (routes().cart_url || '/cart') + '.js?t=' + Date.now();
    var res = await fetch(url, { headers: { 'Content-Type': 'application/json' }, cache: 'no-store' });
    return res.json();
  }

  // Eén regel wijzigen via /cart/change.js — exact hetzelfde mechanisme dat de
  // theme zelf voor quantity gebruikt, zodat de cart betrouwbaar herrendert.
  async function applyChange(lineKey, quantity) {
    var body = JSON.stringify({ id: lineKey, quantity: quantity, sections: bundledSections() });
    var res = await fetch(routes().cart_change_url || '/cart/change.js', {
      method: 'POST',
      // Accept: application/json is essentieel — zonder deze header geeft Shopify
      // een HTML-response terug en klapt JSON.parse (dan loopt de fee 1 achter).
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body,
    });
    return res.json();
  }

  async function reconcile() {
    // Loopt er al een reconcile? Onthoud dat er opnieuw gedraaid moet worden
    // zodra die klaar is (zo lopen we nooit een stap achter).
    if (busy) {
      pending = true;
      return;
    }

    busy = true;

    try {
      var guard = 0;
      do {
        pending = false;

        if (++guard > 8) break; // veiligheidsklep tegen een oneindige lus

        var cart = await getCart();
        if (!cart || !Array.isArray(cart.items)) break;

        var updates = computeUpdates(cart.items);
        var keys = Object.keys(updates);
        if (keys.length === 0) break;

        var state;
        for (var i = 0; i < keys.length; i++) {
          state = await applyChange(keys[i], updates[keys[i]]);
        }

        if (state && window.FoxTheme && window.FoxTheme.pubsub) {
          window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: state });
        }
        // Kwam er ondertussen een nieuwe wijziging binnen? Dan nog een ronde.
      } while (pending);
    } catch (err) {
      console.warn('[Naamopdruk] Kon cart-koppeling niet bijwerken:', err);
    } finally {
      busy = false;
    }
  }

  // Na elke cart-wijziging opnieuw uitlijnen. De publish hierboven triggert dit
  // nog een keer, maar dan is de cart al consistent en gebeurt er niets meer.
  document.addEventListener('cart:updated', function () {
    reconcile();
  });

  // Ook bij het openen van een cart-pagina waar de cart al scheef staat.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      reconcile();
    });
  } else {
    reconcile();
  }
})();
